import { useState, useEffect } from 'react';
import { useProviderSearch } from '../hooks/useProviderSearch';
import { useSyncUrlSearchFilters, useUrlSearchFilters } from '../hooks/useUrlSearchFilters';
import { useAllTaxonomy } from '../hooks/useTaxonomy';
import { useAuth } from '../../authentication/hooks/useAuth';
import { useWorkspace } from '@/features/workspace-sub-delegation';
import { useUnauthRumAction } from '../../pre-login/hooks/useUnauthRumAction';
import { ProviderDirectoryFilters } from './ProviderDirectoryFilters';
import { ProviderDirectoryResults } from './ProviderDirectoryResults';
import { FinancialAdvisorSearchComingSoon } from './FinancialAdvisorSearchComingSoon';
import { FilterTags } from './FilterTags';
import type { ProviderType, SearchFilters, ServiceRange, SearchProvidersResponse } from '../types';
import { buildProviderDirectoryFilterTags } from '../utils/providerDirectoryFilterTags';

const isFinancialAdvisorSearchHidden = () =>
  import.meta.env.VITE_HIDE_FINANCIAL_ADVISOR_SEARCH !== 'false';

// A filtered link opens a clean search (defaults + URL filters), mounted once its ids resolve.
export const ProviderDirectory = () => {
  const { isResolving, filters: urlFilters } = useUrlSearchFilters();
  if (isResolving) {
    return <p role="status" className="min-h-screen bg-background p-8">Loading search…</p>;
  }
  return <ProviderDirectorySearch urlFilters={urlFilters} />;
};

const ProviderDirectorySearch = ({ urlFilters }: { urlFilters: Partial<SearchFilters> | null }) => {
  const { user, isLoading: isAuthLoading, isAuthenticated } = useAuth();
  const { effectiveRole } = useWorkspace();
  const trackAction = useUnauthRumAction();

  // FA exclusion (FR-124) goes last so nothing from the URL can override it.
  const initialFilters: Partial<SearchFilters> = {
    providerType: 'SERVICE_PROVIDER',
    page: 1,
    limit: 10,
    ...urlFilters,
    ...(user?.role === 'FINANCIAL_ADVISOR' && { excludeRole: 'FINANCIAL_ADVISOR' }),
  };

  const {
    providers,
    searchContextId,
    total,
    page,
    limit,
    totalPages,
    isLoading,
    isError,
    filters,
    updateFilters,
    resetFilters,
  } = useProviderSearch(user?.kindeId, initialFilters, isAuthLoading, {
    skipStoredFilters: urlFilters !== null,
  });
  useSyncUrlSearchFilters(filters);

  const {
    categories,
    specialties,
    isLoading: isTaxonomyLoading,
  } = useAllTaxonomy();

  // Feature 017: Provider name search with debounce
  const [providerNameInput, setProviderNameInput] = useState<string>(filters.providerName || '');

  // Feature 017: Request Help modal state

  // Scroll to top when page changes
  useEffect(() => {
    if (filters.page && filters.page > 1) {
      window.scrollTo(0, 0);
    }
  }, [filters.page]);

  // Feature 017: Debounce provider name search (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (providerNameInput !== (filters.providerName || '')) {
        updateFilters({ providerName: providerNameInput || undefined, page: 1 });
        // Guest-only tracking (spec linha 161). Skip empty + 1-char queries to
        // limit RUM volume on every keystroke debounce.
        if (!isAuthenticated && providerNameInput.trim().length >= 2) {
          trackAction('unauth.search', { tab: 'providers', query: providerNameInput.trim() });
        }
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [providerNameInput, filters.providerName, updateFilters, isAuthenticated, trackAction]);

  // Handle category filter changes
  const handleCategoryChange = (categoryIds: string[]) => {
    updateFilters({ categoryIds, page: 1 });
  };

  // Handle specialty filter changes
  const handleSpecialtyChange = (specialtyIds: string[]) => {
    updateFilters({ specialtyIds, page: 1 });
  };

  // Feature 017: handleExpertiseChange removed - expertise filter no longer used

  // Handle service range checkbox changes
  const handleServiceRangeToggle = (range: ServiceRange) => {
    const currentRanges = filters.serviceRanges || [];
    const isSelected = currentRanges.includes(range);

    let newRanges: ServiceRange[];
    if (isSelected) {
      newRanges = currentRanges.filter((r) => r !== range);
    } else {
      newRanges = [...currentRanges, range];
    }

    updateFilters({ serviceRanges: newRanges, page: 1 });
  };

  // Handle location selection
  const handleLocationSelect = (location: { address: string; latitude: number; longitude: number; countyId?: string; stateCode?: string } | null) => {
    if (location) {
      updateFilters({
        locationAddress: location.address,
        latitude: location.latitude,
        longitude: location.longitude,
        countyId: location.countyId,
        stateCode: location.stateCode,
        page: 1,
      });
    } else {
      updateFilters({
        locationAddress: undefined,
        latitude: undefined,
        longitude: undefined,
        countyId: undefined,
        stateCode: undefined,
        page: 1,
      });
    }
  };

  // Feature 017: Handle provider name search input
  const handleProviderNameChange = (name: string) => {
    setProviderNameInput(name);
  };

  const handleProviderTypeChange = (providerType: ProviderType) => {
    if (providerType === 'FINANCIAL_ADVISOR') {
      updateFilters({
        providerType,
        categoryIds: [],
        specialtyIds: [],
        expertiseIds: [],
        serviceRanges: [],
        newClientAcceptance: undefined,
        page: 1,
      });
      return;
    }

    updateFilters({ providerType, page: 1 });
  };

  // Override resetFilters to also clear provider name input
  const handleResetFilters = () => {
    setProviderNameInput('');
    resetFilters();
  };

  // Handle filter tag removal
  const handleFilterTagRemove = (tagId: string) => {
    if (tagId === 'provider-name') {
      // Feature 017: Clear provider name search
      setProviderNameInput('');
      updateFilters({ providerName: undefined, page: 1 });
    } else if (tagId.startsWith('category-')) {
      const categoryId = tagId.replace('category-', '');
      handleCategoryChange((filters.categoryIds || []).filter((id) => id !== categoryId));
    } else if (tagId.startsWith('specialty-')) {
      const specialtyId = tagId.replace('specialty-', '');
      handleSpecialtyChange((filters.specialtyIds || []).filter((id) => id !== specialtyId));
    } else if (tagId === 'location') {
      handleLocationSelect(null);
    }
    // Feature 017: Expertise tag removal removed - expertise filter no longer used
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    updateFilters({ page: newPage });
  };

  const handleRetry = () => {
    // Force refetch by updating filters
    updateFilters({});
  };

  // Build search result object for the Results component. The backend always
  // sends `searchContextId` (see SearchProvidersResponseDto), so treat a
  // resolved query missing it the same as still-loading rather than passing
  // through `undefined` where the type requires `string`.
  const searchResult: SearchProvidersResponse | null =
    isLoading || isError || !searchContextId
      ? null
      : {
          searchContextId,
          providers,
          total,
          page,
          limit,
          totalPages,
        };

  // Feature 104: care-seeker-audience gating uses the workspace effective role
  // (delegation-aware, same computation as ProviderDirectoryResults); logged-out
  // visitors count as care-seeker audience.
  const effectiveViewingRole = effectiveRole ?? user?.role;
  const isCareSeekerAudience = !effectiveViewingRole || effectiveViewingRole === 'CARE_SEEKER';
  const filterTags = buildProviderDirectoryFilterTags({ filters, categories, specialties });
  const hasActiveFilters = filterTags.length > 0;
  const shouldShowFinancialAdvisorComingSoon =
    filters.providerType === 'FINANCIAL_ADVISOR' && isFinancialAdvisorSearchHidden();

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8 flex justify-between items-start">
          <div>
            <h1 className="text-3xl text-gray-900 mb-2">Provider Directory</h1>
            <p className="text-gray-600">Find verified care providers in your area</p>
          </div>

        </div>

      <div>
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Filters Sidebar */}
          <div className="lg:col-span-1">
            <ProviderDirectoryFilters
              filters={filters}
              categories={categories}
              specialties={specialties}
              isTaxonomyLoading={isTaxonomyLoading}
              locationAddress={filters.locationAddress || ''}
              providerNameInput={providerNameInput}
              providerType={filters.providerType ?? 'SERVICE_PROVIDER'}
              currentUserRole={user?.role}
              onCategoryChange={handleCategoryChange}
              onSpecialtyChange={handleSpecialtyChange}
              onServiceRangeToggle={handleServiceRangeToggle}
              onLocationSelect={handleLocationSelect}
              onProviderNameChange={handleProviderNameChange}
              onProviderTypeChange={handleProviderTypeChange}
            />
          </div>

          {/* Results */}
          <div className="lg:col-span-3">
            {/* Filter Tags */}
            {hasActiveFilters && (
              <div className="mb-6">
                <FilterTags
                  filters={filterTags}
                  onRemove={handleFilterTagRemove}
                  onClearAll={handleResetFilters}
                  showClearAll
                  showCount
                />
              </div>
            )}

            {/* Results Section */}
            {shouldShowFinancialAdvisorComingSoon ? (
              <FinancialAdvisorSearchComingSoon />
            ) : (
              <ProviderDirectoryResults
                isCareSeekerAudience={isCareSeekerAudience}
                searchResult={searchResult}
                isLoading={isLoading}
                isError={isError}
                hasActiveFilters={hasActiveFilters}
                onPageChange={handlePageChange}
                onRetry={handleRetry}
                onClearFilters={handleResetFilters}
                searchCategoryNames={
                  filters.categoryIds?.length
                    ? categories
                        .filter((c) => filters.categoryIds!.includes(c.id))
                        .map((c) => c.name)
                    : undefined
                }
              />
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};
